import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { buildCertificateFileBaseName, buildCertificatePdfFileName, createCertificatePdfBlob } from '../utils/certificatePdf';

interface GenerateCertificateModalProps {
  isOpen: boolean;
  certificateText: string;
  onClose: () => void;
  templateTitle?: string;
  recipientName?: string;
  onGenerateWithAI?: (editedTemplateText: string, instructions?: string) => Promise<{ text: string; provider: string }>;
}

const downloadBlob = (blob: Blob, fileName: string) => {
  const element = document.createElement('a');
  const objectUrl = URL.createObjectURL(blob);
  element.href = objectUrl;
  element.download = fileName;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const AI_INSTRUCTIONS_PLACEHOLDER =
  'Ex.: reescreva em linguagem mais formal, reduza repetições, melhore a fluidez e acrescente apenas o que eu pedir.';

export const GenerateCertificateModal: React.FC<GenerateCertificateModalProps> = ({
  isOpen,
  certificateText,
  onClose,
  templateTitle = 'Certidão',
  recipientName,
  onGenerateWithAI,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(certificateText);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showAIInstructions, setShowAIInstructions] = useState(false);
  const [aiInstructions, setAIInstructions] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEditedText(certificateText);
      setIsEditing(false);
      setShowAIInstructions(false);
      setAIInstructions('');
    }
  }, [isOpen, certificateText]);

  if (!isOpen) return null;

  const copyCertificate = async () => {
    await navigator.clipboard.writeText(editedText);
    toast.success('Certidão copiada para a área de transferência');
  };

  const downloadCertificate = () => {
    downloadBlob(
      new Blob([editedText], { type: 'text/plain' }),
      `${buildCertificateFileBaseName(recipientName, templateTitle)}.txt`
    );
    toast.success('Certidão baixada com sucesso');
  };

  const downloadCertificatePdf = () => {
    const pdfBlob = createCertificatePdfBlob(editedText, templateTitle);
    downloadBlob(pdfBlob, buildCertificatePdfFileName(recipientName));
    toast.success('PDF baixado com sucesso');
  };

  const printCertificate = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>${templateTitle}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; line-height: 1.6; }
            pre { white-space: pre-wrap; word-wrap: break-word; }
          </style>
        </head>
        <body>
          <pre>${editedText}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const generateWithAI = async (instructions?: string) => {
    if (!onGenerateWithAI) {
      toast.error('Geração com IA indisponível');
      return;
    }

    try {
      setGeneratingAI(true);
      const result = await onGenerateWithAI(editedText, instructions?.trim());
      if (result.text && result.text.trim()) {
        setEditedText(result.text);
        setIsEditing(true);
        const providerLabel =
          result.provider === 'openai'
            ? 'ChatGPT'
            : result.provider === 'groq'
              ? 'Groq'
              : result.provider === 'claude'
                ? 'Claude'
                : result.provider === 'gemini'
                  ? 'Gemini'
                  : result.provider;
        toast.success(`Certidão revisada com ${providerLabel}`);
      } else {
        toast.error('A IA não retornou texto. Verifique a configuração do provedor.');
      }
    } catch (error) {
      console.error('Erro ao gerar certidão com IA:', error);
      const message = error instanceof Error ? error.message : 'Erro ao gerar certidão com IA';
      toast.error(message);
    } finally {
      setGeneratingAI(false);
    }
  };

  const openAIInstructions = () => {
    if (!isEditing) {
      setIsEditing(true);
    }

    if (!onGenerateWithAI) {
      toast.error('Geração com IA indisponível');
      return;
    }

    setShowAIInstructions(true);
  };

  const submitAIInstructions = async () => {
    if (!aiInstructions.trim()) {
      toast.error('Informe as instruções para a IA');
      return;
    }

    setShowAIInstructions(false);
    await generateWithAI(aiInstructions);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="flex max-h-[95vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900">{templateTitle}</h2>
          <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-700">
            x
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {isEditing && (
            <div className="mb-4 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-800">
              Modo de edição ativo. Use "Gerar com I.A." para refinar o texto mantendo o contexto jurídico.
            </div>
          )}

          {isEditing ? (
            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              className="min-h-[70vh] w-full resize-y rounded border border-gray-300 bg-white p-4 text-sm leading-relaxed text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <div className="whitespace-pre-wrap rounded border border-gray-300 bg-white p-6 text-sm leading-relaxed text-gray-800">
              {editedText}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-gray-500">Gerada em {new Date().toLocaleString()}</div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            <button
              onClick={onClose}
              className="rounded bg-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-400"
            >
              Fechar
            </button>
            <button
              onClick={() => setIsEditing((current) => !current)}
              className={`rounded px-4 py-2 text-sm font-medium ${
                isEditing ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {isEditing ? 'Salvar' : 'Editar'}
            </button>
            {isEditing && (
              <button
                onClick={openAIInstructions}
                disabled={!onGenerateWithAI || generatingAI}
                className="rounded bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {generatingAI ? 'Gerando com I.A....' : 'Gerar com I.A.'}
              </button>
            )}
            <button
              onClick={printCertificate}
              className="rounded bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600"
            >
              Imprimir
            </button>
            <button
              onClick={downloadCertificate}
              className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              Baixar
            </button>
            <button
              onClick={downloadCertificatePdf}
              className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              Baixar PDF
            </button>
            <button
              onClick={copyCertificate}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Copiar
            </button>
          </div>
        </div>
      </div>

      {showAIInstructions && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Instruções para a IA</h3>
              <p className="mt-1 text-sm text-gray-500">
                A IA irá gerar o texto final usando o modelo editado como base principal e as instruções abaixo como direcionamento obrigatório.
              </p>
            </div>

            <div className="space-y-4 px-6 py-5">
              <label className="block text-sm font-medium text-gray-700" htmlFor="ai-instructions">
                O que você quer que a IA faça?
              </label>
              <textarea
                id="ai-instructions"
                value={aiInstructions}
                onChange={(event) => setAIInstructions(event.target.value)}
                className="min-h-40 w-full rounded-lg border border-gray-300 bg-white p-4 text-sm leading-relaxed text-gray-800 focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder={AI_INSTRUCTIONS_PLACEHOLDER}
              />
              <div className="rounded-lg border border-violet-100 bg-violet-50 px-4 py-3 text-sm text-violet-900">
                Dica: descreva exatamente como quer a revisão. Quanto mais claro for o pedido, mais fiel a IA tende a ficar ao que você pediu.
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowAIInstructions(false)}
                className="rounded bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitAIInstructions}
                disabled={generatingAI}
                className="rounded bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {generatingAI ? 'Gerando...' : 'Gerar revisão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
